import { z } from 'zod';
import { EncryptedArtifactRef } from './artifact-ref.js';
import { BarkAttachment } from './bark.js';
import { PrivateEntityId } from './encrypted-entities.js';
import { PrivateProjectBinding } from './private-project.js';
import { OPTION_MEDIA_MAX, OptionComments, OptionMedia, OptionMix, OptionSet, } from './visual-review.js'; // visual-review
/**
 * Decisions (docs/ux/project-tab-spec.md §1.6, §3.4). One `decision` entity is one ask an agent
 * raised through `viko_ask`: the full question, options with rationale and trade-offs, evidence,
 * and a conversation thread with the asking agent. Everything below is sealed inside the
 * encrypted entity; the hub sees an opaque entity head and, for routing, the command envelope's
 * machine id. `decisionProjection` is the only hub-visible shape (ids, enums, counters, times).
 *
 * Writers: the asking machine's daemon creates the entity, appends agent replies, revisions and
 * delivery receipts; devices append user messages and answers. Arrays upsert by id and terminal
 * states never regress, so concurrent writers merge (`mergeDecision`).
 */
const Id = PrivateEntityId;
const LocalId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_-]+$/);
const At = z.number().int().nonnegative();
const RunId = z.string().min(1).max(256);
export const DECISION_TEXT_MAX = 20_000;
export const DECISION_THREAD_MAX = 200;
export const DECISION_OPTIONS_MAX = 9;
export const DECISION_EVIDENCE_MAX = 30;
export const DecisionCategory = z.enum([
    'taste',
    'product',
    'irreversible',
    'money',
    'secrets',
    'security',
    'release',
    'other',
]);
/** Answering one of these is a risky command (a passkey step-up), whatever the device. */
export const RISKY_DECISION_CATEGORIES = [
    'secrets',
    'security',
    'release',
    'irreversible',
];
export function isRiskyDecisionCategory(c) {
    return RISKY_DECISION_CATEGORIES.includes(c);
}
/** The command kind a device signs to answer: risky categories go through the step-up kind. */
export function decisionAnswerKind(c) {
    return isRiskyDecisionCategory(c) ? 'decision.answer.risky' : 'decision.answer';
}
export const DecisionOption = z.strictObject({
    id: LocalId,
    label: z.string().min(1).max(200),
    /** Longer description (the CLI question's option description). */
    description: z.string().max(2000).optional(),
    rationale: z.string().max(2000),
    tradeoffs: z.strictObject({
        pro: z.array(z.string().min(1).max(500)).max(10),
        con: z.array(z.string().min(1).max(500)).max(10),
    }),
    reversible: z.boolean(),
    recommended: z.boolean(),
    // ---- visual-review: images / HTML boards of a design option ----
    media: z.array(OptionMedia).max(OPTION_MEDIA_MAX).optional(),
    // ---- end visual-review ----
});
export const DecisionEvidenceKind = z.enum([
    'artifact',
    'diff',
    'test',
    'commit',
    'transcript',
    'link',
]);
export const DecisionEvidence = z.strictObject({
    kind: DecisionEvidenceKind,
    /** Path, commit sha, artifact id, `seq:<n>` or URL: what the openable reference resolves. */
    ref: z.string().min(1).max(2000),
    label: z.string().max(200),
    /** Diff hunk, failing test output or a short excerpt shown inline. */
    excerpt: z.string().max(8000).optional(),
    status: z.enum(['pass', 'fail', 'running']).optional(),
    runId: RunId.optional(),
    seq: z.number().int().positive().optional(),
    /** Sealed artifact (screenshots, videos, builds) opened by the encrypted viewer. */
    artifact: EncryptedArtifactRef.optional(),
    mime: z.string().max(255).optional(),
    /**
     * app-clarity carry-over (b): the agent flagged this as directly relevant to the answer (shown
     * large in the decision's focus area) or as background context (behind "Context provided").
     */
    relevance: z.enum(['direct', 'context']).optional(),
});
export const DecisionDelivery = z.strictObject({
    status: z.enum(['sent', 'queued', 'delivered', 'resumed', 'triage', 'failed']),
    at: At,
    note: z.string().max(500).optional(),
    /** `triage`: the bark the triage agent handles it through. */
    barkId: Id.optional(),
});
export const DecisionThreadMessage = z.strictObject({
    id: LocalId,
    at: At,
    author: z.enum(['user', 'agent', 'triage', 'system']),
    runId: RunId.optional(),
    text: z.string().max(DECISION_TEXT_MAX),
    attachments: z.array(BarkAttachment).max(20).optional(),
    /** This agent message revised the options (the UI shows "revised" with a diff toggle). */
    revisedOptions: z.boolean().optional(),
    /** User messages: what happened to it (delivered as a turn, resumed the run, went to triage). */
    delivery: DecisionDelivery.optional(),
});
export const DecisionStatus = z.enum([
    'open',
    'answered',
    'delegated',
    'resolved',
    'withdrawn',
    'superseded',
    'expired',
]);
export const DecisionAnswerMode = z.enum(['pick', 'freeform', 'delegate']);
export const DecisionRecord = z.strictObject({
    id: Id,
    projectId: Id,
    runId: RunId.optional(),
    /** The asking run's machine: where `decision.message` / `decision.answer` are routed. */
    machineId: z.string().min(1).max(256).optional(),
    agent: z.enum(['claude', 'codex']).optional(),
    taskId: Id.optional(),
    workstreamId: Id.optional(),
    barkId: Id.optional(),
    /** `vq_…` (viko_ask) or CLI question id; links to the transcript event. */
    questionId: z.string().min(1).max(256).optional(),
    /** Transcript anchor of the question event. */
    seq: z.number().int().positive().optional(),
    /** Short title (the ask's header), ≤120. */
    title: z.string().max(120).optional(),
    question: z.string().min(1).max(4000),
    why: z.string().max(4000),
    /**
     * app-clarity carry-over (a): 2–3 one-line points on how it got here (done/tried, the trade-off,
     * what the agent leans to), shown under the question. Written by the ask tool or a summarizer.
     */
    keyPoints: z
        .array(z.strictObject({ label: z.string().max(40), text: z.string().max(280) }))
        .max(3)
        .optional(),
    category: DecisionCategory,
    /** ≤4 options, no evidence required, not risky: quick-pick from rows and pushes. */
    simple: z.boolean(),
    /** The asking run waits for the answer. */
    blocking: z.boolean(),
    /** Tasks this answer unblocks (leverage). */
    unblocks: z.number().int().nonnegative().max(1000).optional(),
    /** The agent's reversible assumption while it waits. */
    assumption: z.string().max(2000).optional(),
    options: z.array(DecisionOption).max(DECISION_OPTIONS_MAX),
    optionsRevision: z.number().int().nonnegative(),
    /** The options before the latest revision (the diff toggle). */
    previousOptions: z.array(DecisionOption).max(DECISION_OPTIONS_MAX).optional(),
    revisedAt: At.optional(),
    evidence: z.array(DecisionEvidence).max(DECISION_EVIDENCE_MAX),
    thread: z.array(DecisionThreadMessage).max(DECISION_THREAD_MAX),
    status: DecisionStatus,
    answer: z
        .strictObject({
        mode: DecisionAnswerMode,
        optionId: LocalId.optional(),
        text: z.string().max(DECISION_TEXT_MAX).optional(),
        guidance: z.string().max(4000).optional(),
        at: At,
        deviceKeyId: z.string().min(1).max(256),
        // ---- visual-review: a mix of options per area, and per-option comments ----
        mix: OptionMix.optional(),
        comments: OptionComments.optional(),
        // ---- end visual-review ----
        /** viko-call: answered on a call (History shows "via call"). */
        via: z.literal('call').optional(),
    })
        .optional(),
    // ---- visual-review: design-option set (areas, fan-out tasks, sign-off gate) ----
    optionSet: OptionSet.optional(),
    // ---- end visual-review ----
    /** Delegate: what the agent chose and the assumption it reports back. */
    resolution: z
        .strictObject({
        optionId: LocalId.optional(),
        label: z.string().max(200).optional(),
        assumption: z.string().max(2000).optional(),
        at: At,
    })
        .optional(),
    /** Synced across devices ("Later"). */
    snoozedUntil: At.optional(),
    withdrawnReason: z.string().max(2000).optional(),
    createdAt: At,
    updatedAt: At,
});
export function newDecision(i) {
    const category = i.category ?? 'other';
    const options = i.options ?? [];
    const evidence = i.evidence ?? [];
    const simple = (i.simple ?? true) &&
        options.length > 0 &&
        options.length <= 4 &&
        !isRiskyDecisionCategory(category);
    const opt = (k) => (i[k] !== undefined ? { [k]: i[k] } : {});
    return DecisionRecord.parse({
        id: i.id,
        projectId: i.projectId,
        ...opt('runId'),
        ...opt('machineId'),
        ...opt('agent'),
        ...opt('taskId'),
        ...opt('workstreamId'),
        ...opt('barkId'),
        ...opt('questionId'),
        ...opt('seq'),
        ...(i.title ? { title: i.title.slice(0, 120) } : {}),
        question: i.question.slice(0, 4000),
        why: (i.why ?? '').slice(0, 4000),
        category,
        simple,
        blocking: i.blocking ?? false,
        ...opt('unblocks'),
        ...(i.assumption ? { assumption: i.assumption.slice(0, 2000) } : {}),
        options,
        optionsRevision: 0,
        evidence,
        thread: [],
        status: 'open',
        createdAt: i.now,
        updatedAt: i.now,
    });
}
/** Persisted body: the opaque id lives in the authenticated entity context, not the value. */
export function decisionBody(d) {
    const { id: _id, ...rest } = DecisionRecord.parse(d);
    return rest;
}
/** Hydrate from an authenticated entity row (the context id wins; a mismatching body is refused). */
export function hydrateDecision(row) {
    if (row.value.id !== undefined && row.value.id !== row.id)
        throw new Error('decision identity mismatch');
    return DecisionRecord.parse({ ...row.value, id: row.id });
}
const STATUS_RANK = {
    open: 0,
    answered: 2,
    delegated: 2,
    resolved: 3,
    withdrawn: 3,
    superseded: 3,
    expired: 3,
};
/** Terminal and answered states never regress. */
export function decisionStatusRank(s) {
    return STATUS_RANK[s];
}
export const isOpenDecision = (d) => d.status === 'open';
/** Merge a concurrent writer's view into the latest record (see `mergeBark`). */
export function mergeDecision(latest, mine) {
    const thread = [...latest.thread];
    for (const m of mine.thread) {
        const i = thread.findIndex((x) => x.id === m.id);
        if (i < 0)
            thread.push(m);
        // A later delivery receipt updates the same message.
        else if ((m.delivery?.at ?? 0) >= (thread[i].delivery?.at ?? 0))
            thread[i] = m;
    }
    thread.sort((a, b) => a.at - b.at);
    const evidence = [...latest.evidence];
    for (const e of mine.evidence)
        if (!evidence.some((x) => x.kind === e.kind && x.ref === e.ref))
            evidence.push(e);
    const statusFrom = STATUS_RANK[mine.status] >= STATUS_RANK[latest.status] ? mine.status : latest.status;
    const optionsFrom = mine.optionsRevision >= latest.optionsRevision ? mine : latest;
    const answer = latest.answer ?? mine.answer;
    const resolution = latest.resolution ?? mine.resolution;
    return DecisionRecord.parse({
        ...latest,
        ...mine,
        status: statusFrom,
        options: optionsFrom.options,
        optionsRevision: optionsFrom.optionsRevision,
        ...(optionsFrom.previousOptions ? { previousOptions: optionsFrom.previousOptions } : {}),
        ...(optionsFrom.revisedAt ? { revisedAt: optionsFrom.revisedAt } : {}),
        evidence: evidence.slice(-DECISION_EVIDENCE_MAX),
        thread: thread.slice(-DECISION_THREAD_MAX),
        ...(answer ? { answer } : {}),
        ...(resolution ? { resolution } : {}),
        updatedAt: Math.max(latest.updatedAt, mine.updatedAt),
    });
}
/** Replace the options (agent revision), keeping the previous set for the diff toggle. */
export function revisedOptions(d, options, now) {
    return {
        ...d,
        previousOptions: d.options,
        options,
        optionsRevision: d.optionsRevision + 1,
        revisedAt: now,
        updatedAt: Math.max(now, d.updatedAt),
    };
}
/** MCP option input → a stored option with a stable id per label within one revision. */
export function toDecisionOptions(opts, revision = 0) {
    return opts.slice(0, DECISION_OPTIONS_MAX).map((o, n) => ({
        id: `o${revision}_${n + 1}`,
        label: o.label.trim().slice(0, 200) || `Option ${n + 1}`,
        ...(o.description ? { description: o.description.slice(0, 2000) } : {}),
        rationale: (o.rationale ?? '').slice(0, 2000),
        tradeoffs: {
            pro: (o.pros ?? [])
                .filter(Boolean)
                .slice(0, 10)
                .map((s) => s.slice(0, 500)),
            con: (o.cons ?? [])
                .filter(Boolean)
                .slice(0, 10)
                .map((s) => s.slice(0, 500)),
        },
        reversible: o.reversible ?? true,
        recommended: o.recommended ?? /\((recommended|suggested)\)/i.test(o.label),
    }));
}
/**
 * Hub-visible projection (the inbox ranking works on metadata only): ids, bounded enums,
 * counters and timestamps. Never text. Defined so the allowlist is tested; not stored in P2.
 */
export const DecisionProjection = z.strictObject({
    id: Id,
    projectId: Id,
    workstreamId: Id.optional(),
    runId: RunId.optional(),
    kind: z.literal('decision'),
    category: DecisionCategory,
    blocking: z.boolean(),
    unblocks: z.number().int().nonnegative().max(1000),
    runWaiting: z.boolean(),
    simple: z.boolean(),
    status: DecisionStatus,
    createdAt: At,
});
export function decisionProjection(d, x = {}) {
    return DecisionProjection.parse({
        id: d.id,
        projectId: d.projectId,
        ...(d.workstreamId ? { workstreamId: d.workstreamId } : {}),
        ...(d.runId ? { runId: d.runId } : {}),
        kind: 'decision',
        category: d.category,
        blocking: x.blocking ?? d.blocking,
        unblocks: x.unblocks ?? d.unblocks ?? 0,
        runWaiting: x.runWaiting ?? d.blocking,
        simple: d.simple,
        status: d.status,
        createdAt: d.createdAt,
    });
}
// --- commands (additive; spread into COMMAND_PAYLOADS) --------------------------------------------
const AnswerPayload = z
    .object({
    decisionId: Id,
    projectId: Id,
    mode: DecisionAnswerMode,
    optionId: LocalId.optional(),
    text: z.string().max(DECISION_TEXT_MAX).optional(),
    guidance: z.string().max(4000).optional(),
    // ---- visual-review ----
    mix: OptionMix.optional(),
    comments: OptionComments.optional(),
    // ---- end visual-review ----
    /** additive (viko-call): answered on a call; History shows it "via call". */
    callId: PrivateEntityId.optional(),
})
    .refine((p) => p.mode !== 'pick' || !!p.optionId, 'pick needs an option')
    .refine((p) => p.mode !== 'freeform' || !!p.text?.trim(), 'freeform needs text');
/**
 * `decision.message`: a user message in the thread (device-signed, non-risky, sealed). Routed to
 * the asking run's machine; `via: 'triage'` sends it to the project's triage machine instead
 * (the run ended and cannot be resumed). `messageId` is the idempotency key.
 * `decision.answer`: pick / freeform / delegate. Risky categories are answered with
 * `decision.answer.risky` (passkey step-up); the daemon refuses the plain kind for them.
 */
export const DECISION_COMMAND_PAYLOADS = {
    'decision.message': z
        .object({
        decisionId: Id,
        projectId: Id,
        messageId: LocalId,
        text: z.string().min(1).max(DECISION_TEXT_MAX),
        attachments: z.array(BarkAttachment).max(20).optional(),
        via: z.enum(['run', 'triage']).optional(),
        privateProject: PrivateProjectBinding.optional(),
        /** additive (viko-call): a note added on a call; History shows it "via call". */
        callId: PrivateEntityId.optional(),
    })
        .refine((p) => !p.privateProject || p.privateProject.snapshot.id === p.projectId, 'private project mismatch'),
    'decision.answer': AnswerPayload,
    'decision.answer.risky': AnswerPayload,
};
// --- MCP (additive) -------------------------------------------------------------------------------
/** `viko_ask` / `viko_decision_reply` option input (label-only inputs keep working). */
export const AskOptionInput = z.object({
    /** Unbounded as before (additive); clipped to 200 when stored. */
    label: z.string(),
    description: z.string().optional(),
    rationale: z.string().max(4000).optional(),
    pros: z.array(z.string()).max(10).optional(),
    cons: z.array(z.string()).max(10).optional(),
    reversible: z.boolean().optional(),
    recommended: z.boolean().optional(),
});
export const AskEvidenceInput = z.object({
    kind: DecisionEvidenceKind,
    ref: z.string().min(1).max(2000),
    label: z.string().max(200),
    excerpt: z.string().max(8000).optional(),
    status: z.enum(['pass', 'fail', 'running']).optional(),
});
export const VikoDecisionReplyInput = z.object({
    decisionId: Id,
    /** Reply to the user in the decision's thread. */
    text: z.string().min(1).max(DECISION_TEXT_MAX),
    /** Revised options (replaces the set; the UI shows "revised" with a diff toggle). */
    options: z.array(AskOptionInput).max(DECISION_OPTIONS_MAX).optional(),
    evidence: z.array(AskEvidenceInput).max(DECISION_EVIDENCE_MAX).optional(),
    /** Withdraw the ask (no longer needed); `text` says why. */
    withdraw: z.boolean().optional(),
});
export const VikoDecisionReplyOutput = z.object({
    ok: z.boolean(),
    messageId: LocalId,
    optionsRevision: z.number().int().nonnegative(),
    status: DecisionStatus,
});
export const VikoDecisionResolveInput = z.object({
    decisionId: Id,
    /** The option the agent chose (after "Decide for me"), by id or label. */
    optionId: LocalId.optional(),
    label: z.string().max(200).optional(),
    /** The assumption reported back to the user (shown as an assumption card). */
    assumption: z.string().min(1).max(2000),
});
export const VikoDecisionResolveOutput = z.object({ ok: z.boolean(), status: DecisionStatus });
export const DECISION_MCP_TOOLS = {
    viko_decision_reply: { input: VikoDecisionReplyInput, output: VikoDecisionReplyOutput },
    viko_decision_resolve: { input: VikoDecisionResolveInput, output: VikoDecisionResolveOutput },
};
/** Thread messages delivered to a live run start with this marker (the agent answers in-thread). */
export const decisionThreadPrefix = (decisionId) => `[Decision ${decisionId} thread]`;
//# sourceMappingURL=decision.js.map